// src/OrdenClienteExistente.js
import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

const borde = '#e5e7eb';
const negro = '#111827';
const naranja = '#f97316';

const inputStyle = {
  display: 'block',
  width: '100%',
  padding: '6px 8px',
  borderRadius: 8,
  border: `1px solid ${borde}`,
  marginBottom: 6,
  fontSize: 14,
};

const labelStyle = {
  fontWeight: 600,
  marginTop: 8,
  marginBottom: 4,
  fontSize: 14,
};

export default function OrdenClienteExistente() {
  const [clienteId, setClienteId] = useState('');
  const [vehiculoId, setVehiculoId] = useState('');
  const [clientes, setClientes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [orden, setOrden] = useState({
    descripcion_falla: '',
    observaciones: '',
    monto: '',
  });
  const [historial, setHistorial] = useState([]);
  const [mensaje, setMensaje] = useState('');
  const [cargando, setCargando] = useState(false);

  // Cargar clientes al inicio
  useEffect(() => {
    const cargarClientes = async () => {
      const { data, error } = await supabase
        .from('clientes')
        .select('id, nombre')
        .order('nombre', { ascending: true });
      if (error) {
        console.error(error);
        setMensaje('Error al cargar clientes: ' + error.message);
        return;
      }
      setClientes(data);
    };

    cargarClientes();
  }, []);

  // Cargar vehículos cuando cambie clienteId
  useEffect(() => {
    const cargarVehiculos = async () => {
      if (!clienteId) {
        setVehiculos([]);
        setVehiculoId('');
        return;
      }
      const { data, error } = await supabase
        .from('vehiculos')
        .select('id, patente, marca, modelo, anio')
        .eq('cliente_id', clienteId)
        .order('patente', { ascending: true });

      if (error) {
        console.error(error);
        setMensaje('Error al cargar vehículos: ' + error.message);
        return;
      }
      setVehiculos(data);
      setVehiculoId(data[0]?.id || '');
    };

    cargarVehiculos();
  }, [clienteId]);

  // Cargar historial de órdenes cuando cambie clienteId
  useEffect(() => {
    const cargarHistorial = async () => {
      if (!clienteId) {
        setHistorial([]);
        return;
      }
      const { data, error } = await supabase
        .from('ordenes_trabajo')
        .select('id, descripcion_falla, estado, fecha_ingreso, monto')
        .eq('cliente_id', clienteId)
        .order('fecha_ingreso', { ascending: false })
        .limit(10);

      if (error) {
        console.error(error);
        setMensaje('Error al cargar historial: ' + error.message);
        return;
      }
      setHistorial(data);
    };

    cargarHistorial();
  }, [clienteId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje('');
    setCargando(true);
    try {
      if (!clienteId) throw new Error('Selecciona un cliente.');
      if (!vehiculoId) throw new Error('Selecciona un vehículo.');
      if (!orden.descripcion_falla) throw new Error('Ingresa la falla.');

      const { error } = await supabase.from('ordenes_trabajo').insert({
        cliente_id: clienteId,
        vehiculo_id: vehiculoId,
        descripcion_falla: orden.descripcion_falla,
        observaciones: orden.observaciones || null,
        estado: 'pendiente',
        fecha_ingreso: new Date().toISOString(),
        monto: orden.monto ? Number(orden.monto) : null,
      });

      if (error) throw new Error('Error al crear orden: ' + error.message);

      setMensaje('Orden creada para cliente existente.');
      setOrden({
        descripcion_falla: '',
        observaciones: '',
        monto: '',
      });

      // Recargar historial después de crear la orden
      const { data: histData, error: histErr } = await supabase
        .from('ordenes_trabajo')
        .select('id, descripcion_falla, estado, fecha_ingreso, monto')
        .eq('cliente_id', clienteId)
        .order('fecha_ingreso', { ascending: false })
        .limit(10);

      if (!histErr && histData) {
        setHistorial(histData);
      }
    } catch (err) {
      setMensaje(err.message);
    } finally {
      setCargando(false);
    }
  };

  return (
    <section style={{ marginTop: 24 }}>
      <h2 style={{ marginTop: 0, marginBottom: 12, color: negro }}>
        Nueva orden a cliente existente
      </h2>

      <form onSubmit={handleSubmit}>
        {/* Selección de cliente */}
        <label style={labelStyle}>Cliente</label>
        <select
          style={inputStyle}
          value={clienteId}
          onChange={(e) => setClienteId(e.target.value)}
        >
          <option value="">Selecciona un cliente</option>
          {clientes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nombre}
            </option>
          ))}
        </select>

        {/* Selección de vehículo */}
        <label style={labelStyle}>Vehículo</label>
        <select
          style={inputStyle}
          value={vehiculoId}
          onChange={(e) => setVehiculoId(e.target.value)}
          disabled={!clienteId}
        >
          <option value="">
            {clienteId ? 'Selecciona un vehículo' : 'Primero elige un cliente'}
          </option>
          {vehiculos.map((v) => (
            <option key={v.id} value={v.id}>
              {(v.patente || 'Sin patente') +
                ' · ' +
                (v.marca || '') +
                ' ' +
                (v.modelo || '') +
                (v.anio ? ` (${v.anio})` : '')}
            </option>
          ))}
        </select>

        {/* Datos de la orden */}
        <h3 style={{ marginTop: 16, marginBottom: 4, color: negro }}>Orden</h3>

        <label style={labelStyle}>Descripción de la falla</label>
        <textarea
          style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
          placeholder="Descripción de la falla"
          value={orden.descripcion_falla}
          onChange={(e) =>
            setOrden({ ...orden, descripcion_falla: e.target.value })
          }
        />

        <label style={labelStyle}>Observaciones</label>
        <textarea
          style={{ ...inputStyle, minHeight: 40, resize: 'vertical' }}
          placeholder="Observaciones"
          value={orden.observaciones}
          onChange={(e) =>
            setOrden({ ...orden, observaciones: e.target.value })
          }
        />

        <label style={labelStyle}>Monto (CLP)</label>
        <input
          style={inputStyle}
          type="number"
          min="0"
          placeholder="Ej: 50000"
          value={orden.monto}
          onChange={(e) =>
            setOrden({ ...orden, monto: e.target.value })
          }
        />

        <button
          type="submit"
          disabled={cargando}
          style={{
            marginTop: 12,
            padding: '8px 14px',
            borderRadius: 999,
            border: 'none',
            backgroundColor: naranja,
            color: 'white',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          {cargando ? 'Guardando...' : 'Crear orden'}
        </button>
      </form>

      {mensaje && <p style={{ marginTop: 10 }}>{mensaje}</p>}

      {/* Historial del cliente */}
      <h3 style={{ marginTop: 24, marginBottom: 8, color: negro }}>
        Historial del cliente
      </h3>

      {!clienteId && (
        <p style={{ fontSize: 13, color: '#6b7280' }}>
          Selecciona un cliente para ver su historial.
        </p>
      )}

      {clienteId && historial.length === 0 && (
        <p style={{ fontSize: 13, color: '#6b7280' }}>
          Este cliente no tiene órdenes anteriores.
        </p>
      )}

      {clienteId && historial.length > 0 && (
        <div
          style={{
            marginTop: 4,
            borderRadius: 8,
            border: `1px solid ${borde}`,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr',
              gap: 8,
              padding: '6px 8px',
              borderBottom: `1px solid ${borde}`,
              fontSize: 12,
              fontWeight: 600,
              color: '#4b5563',
            }}
          >
            <span>Falla</span>
            <span>Estado</span>
            <span>Monto</span>
          </div>

          {historial.map((h) => (
            <div
              key={h.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr',
                gap: 8,
                padding: '6px 8px',
                borderBottom: `1px solid ${borde}`,
                fontSize: 12,
              }}
            >
              <span>{h.descripcion_falla}</span>
              <span>{h.estado}</span>
              <span>
                {h.monto != null
                  ? `$${h.monto.toLocaleString('es-CL')}`
                  : '-'}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
